import React, { useEffect, useState } from "react";

const resolveMediaUrl = (url) => {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (
    trimmed.startsWith("blob:") ||
    trimmed.startsWith("data:") ||
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://")
  ) {
    return trimmed;
  }
  const base = (process.env.REACT_APP_API_URL || "http://127.0.0.1:8000").replace(
    /\/$/,
    ""
  );
  return trimmed.startsWith("/") ? `${base}${trimmed}` : `${base}/${trimmed}`;
};

const getFileFromValue = (value) => {
  if (value instanceof File || value instanceof Blob) return value;
  if (value?.file instanceof File || value?.file instanceof Blob) return value.file;
  return null;
};

const getUrlFromValue = (value, fileUrl) => {
  if (typeof fileUrl === "string" && fileUrl.trim()) return fileUrl;
  if (typeof value?.image_url === "string" && value.image_url.trim()) {
    return value.image_url;
  }
  if (typeof value === "string" && value.trim()) return value;
  return null;
};

const FilePreview = ({
  file,
  fileUrl,
  label,
  onDelete,
  isImage = false,
  className = "",
  showDelete = false,
  compact = false,
  embedded = false,
}) => {
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    const fileObj = getFileFromValue(file);
    const urlCandidate = getUrlFromValue(file, fileUrl);
    let blobUrl = null;

    if (urlCandidate) {
      setImgSrc(resolveMediaUrl(urlCandidate));
    } else if (fileObj) {
      blobUrl = URL.createObjectURL(fileObj);
      setImgSrc(blobUrl);
    } else {
      setImgSrc(null);
    }

    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [file, fileUrl]);

  const fileName =
    getFileFromValue(file)?.name ||
    (typeof file === "object" && file?.name) ||
    label;

  const isPdf =
    fileUrl?.toLowerCase().endsWith(".pdf") ||
    fileName?.toLowerCase().endsWith(".pdf");

  const thumbnail = isImage && imgSrc && (
    <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg ring-1 ring-gray-100">
      <img
        src={imgSrc}
        alt={label || "Preview"}
        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
      />
    </div>
  );

  const pdfIcon = isPdf && (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-red-50 ring-1 ring-red-100">
      <i className="fi fi-rr-file-pdf text-xl text-red-500"></i>
    </div>
  );

  const actions = (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      {imgSrc && (
        <button
          type="button"
          onClick={() => window.open(imgSrc, "_blank")}
          className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-gray-800 transition-colors hover:text-gray-700"
        >
          <i className="fi fi-rr-eye text-[10px]"></i>
          View
        </button>
      )}
      {!showDelete && onDelete && (
        <>
          {imgSrc && <div className="h-1 w-1 shrink-0 rounded-full bg-gray-200"></div>}
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium text-red-500 transition-colors hover:text-red-600"
          >
            <i className="fi fi-rr-trash text-[10px]"></i>
            Remove
          </button>
        </>
      )}
    </div>
  );

  const rootClassName = embedded
    ? `group w-full min-w-0 bg-white ${className}`
    : `group w-full min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white transition-all duration-200 hover:bg-gray-50/80 ${className}`;

  if (compact) {
    return (
      <div className={`${rootClassName} flex flex-col gap-2 px-2.5 py-2`}>
        <div className="flex min-w-0 items-center gap-2.5">
          {thumbnail}
          {pdfIcon}
          <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">
            {label}
          </p>
        </div>
        {actions}
      </div>
    );
  }

  return (
    <div className={`${rootClassName} flex items-center gap-3 px-2.5 py-2`}>
      {thumbnail}
      {pdfIcon}

      <div className="min-w-0 flex-1 py-0.5">
        <p className="truncate text-sm font-medium text-gray-800">{label}</p>
        <div className="mt-1">{actions}</div>
      </div>
    </div>
  );
};

export default FilePreview;
